import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCamera, FiUser, FiPhone, FiLock } from 'react-icons/fi';
import './profile.css';

const Profile = () => {
    const [user, setUser] = useState({ name: "", phone_num: "", profile_image: "" });
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [file, setFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [activeTab, setActiveTab] = useState('profile');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setIsLoading(true);
                const res = await axios.get("https://backend.aubattle.com/api/profile", {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                });
                setUser(res.data);
                setImagePreview(res.data.profile_image || '/images/default_avatar.png');
            } catch (error) {
                console.error("Error fetching profile:", error);
                setError("Failed to load profile data");
            } finally {
                setIsLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleImageChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setImagePreview(URL.createObjectURL(selectedFile));
        }
    };

    const updateProfile = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append("name", user.name);
            formData.append("phone_num", user.phone_num);
            if (file) {
                formData.append("profile_image", file);
            }

            const res = await axios.put(
                "https://backend.aubattle.com/api/profile/update_profile",
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            setUser(res.data.user);
            setMessage("Profile updated successfully");
            setError("");
            setFile(null);
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setError(error.response?.data?.message || "Failed to update profile");
        } finally {
            setIsLoading(false);
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            await axios.put(
                "https://backend.aubattle.com/api/profile/change-password", 
                { oldPassword, newPassword }, 
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );
            setMessage("Password changed successfully");
            setOldPassword("");
            setNewPassword("");
            setError("");
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setError(error.response?.data?.message || "Failed to change password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="profile-page dark-theme">
            <div className="profile-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FiArrowLeft size={20} />
                </button>
                <h1>My Profile</h1>
            </div>

            <div className="profile-container">
                <div className="profile-sidebar">
                    <div className="profile-card">
                        <div className="avatar-upload">
                            <div className="avatar-preview">
                                <img 
                                    src={imagePreview} 
                                    alt="Profile" 
                                    className="profile-avatar"
                                />
                                <label className="upload-overlay">
                                    <FiCamera size={20} />
                                    <input 
                                        type="file" 
                                        onChange={handleImageChange}
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                            <h2>{user.name || 'User'}</h2>
                            <p>{user.phone_num || 'No phone number'}</p>
                        </div>
                    </div>

                    <div className="sidebar-menu">
                        <button 
                            className={`menu-item ${activeTab === 'profile' ? 'active' : ''}`}
                            onClick={() => setActiveTab('profile')}
                        >
                            <FiUser className="menu-icon" />
                            Profile Info
                        </button>
                        <button 
                            className={`menu-item ${activeTab === 'password' ? 'active' : ''}`}
                            onClick={() => setActiveTab('password')}
                        >
                            <FiLock className="menu-icon" />
                            Change Password
                        </button>
                    </div>
                </div>

                <div className="profile-content">
                    {message && (
                        <div className="alert success">
                            {message}
                        </div>
                    )}
                    {error && (
                        <div className="alert error">
                            {error}
                        </div>
                    )}

                    {activeTab === 'profile' ? (
                        <form className="profile-form" onSubmit={updateProfile}>
                            <h2 className="form-title">
                                <FiUser className="title-icon" />
                                Personal Information
                            </h2>
                            
                            <div className="form-group">
                                <label htmlFor="name">Full Name</label>
                                <div className="input-with-icon">
                                    <FiUser className="input-icon" />
                                    <input 
                                        type="text" 
                                        id="name" 
                                        name="name" 
                                        value={user.name} 
                                        onChange={(e) => setUser({...user, name: e.target.value})}
                                        placeholder="Enter your full name"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <div className="input-with-icon">
                                    <FiPhone className="input-icon" />
                                    <input 
                                        type="text" 
                                        id="phone" 
                                        name="phone_num" 
                                        value={user.phone_num} 
                                        onChange={(e) => setUser({...user, phone_num: e.target.value})}
                                        placeholder="Enter your phone number"
                                    />
                                </div>
                            </div>

                            <button type="submit" className="submit-button" disabled={isLoading}>
                                {isLoading ? 'Updating...' : 'Save Changes'}
                            </button>
                        </form>
                    ) : (
                        <form className="password-form" onSubmit={changePassword}>
                            <h2 className="form-title">
                                <FiLock className="title-icon" />
                                Change Password
                            </h2>
                            
                            <div className="form-group">
                                <label htmlFor="oldPassword">Current Password</label>
                                <div className="input-with-icon">
                                    <FiLock className="input-icon" />
                                    <input 
                                        type="password" 
                                        id="oldPassword" 
                                        value={oldPassword} 
                                        onChange={(e) => setOldPassword(e.target.value)}
                                        placeholder="Enter current password"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="newPassword">New Password</label>
                                <div className="input-with-icon">
                                    <FiLock className="input-icon" />
                                    <input 
                                        type="password" 
                                        id="newPassword" 
                                        value={newPassword} 
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                    />
                                </div>
                            </div>

                            <button type="submit" className="submit-button" disabled={isLoading}>
                                {isLoading ? 'Updating...' : 'Change Password'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;